<li
  class="hx-node"
  role="none"
  [class.hx-tree-readonly]="readonly()"
  [class.is-collapsed]="isCollapsed()"
  [ngClass]="containerClass()"
  [style.--level]="level()"
  [style.--line-color]="lineColorVar()">
  <ng-template #statusIcon let-iconClass="iconClass" let-title="title" let-fontIcon="fontIcon" let-filled="filled">
    <div
      class="hx-status-icon"
      [ngClass]="iconClass"
      [attr.title]="title"
      [hxTooltip]="title || ''"
      [attr.aria-label]="title">
      <hx-icon [fontIcon]="fontIcon" [filled]="filled" size="20" />
    </div>
  </ng-template>

  <ng-template #chevron>
    <button
      class="hx-node__chevron"
      tabindex="-1"
      type="button"
      [attr.aria-label]="(isExpanded() ? 'Collapse ' : 'Expand ') + displayLabel()"
      [attr.title]="(isExpanded() ? 'Collapse ' : 'Expand ') + displayLabel()"
      [class.expanded]="isExpanded() && config().chevronPosition !== 'start'"
      [style.flex-shrink]="0"
      (click)="onToggle($event)"
      [stopPropagation]="['click']">
      @if (config().chevronPosition === 'start') {
        <hx-icon [svgIcon]="isExpanded() ? 'ccs-chevron-down' : 'ccs-chevron-right'" size="12" />
      } @else {
        <hx-icon fontIcon="chevron-up" maxHeight="8" maxWidth="8" />
      }
    </button>
  </ng-template>

  @if (!isCollapsed() || level() === 0 || hasChildren() || inferredStatus() !== 'default') {
    <!-- Custom Template -->
    @if (nodeTemplate()) {
      <div
        #nodeContent
        class="hx-node__content"
        role="treeitem"
        [attr.aria-expanded]="hasChildren() ? isExpanded() : null"
        [attr.aria-selected]="isActive()"
        [ngClass]="contentClass()"
        [ngStyle]="customStyles()"
        [tabindex]="isFocused() ? 0 : -1"
        (click)="onContentClick($event)"
        [stopPropagation]="['click']">
        <ng-container
          [ngTemplateOutlet]="nodeTemplate()"
          [ngTemplateOutletContext]="{
            node: node(),
            level: level(),
            isActive: isActive(),
            isExpanded: isExpanded(),
            hasChildren: hasChildren(),
            isFocused: isFocused(),
            isCollapsed: isCollapsed(),
          }" />
      </div>
    } @else {
      <!-- Default Template -->
      <div
        #nodeContent
        class="hx-node__content"
        position="right"
        role="treeitem"
        trigger="hover"
        [appearance]="'pointer'"
        [attr.aria-expanded]="hasChildren() ? isExpanded() : null"
        [attr.aria-selected]="isActive()"
        [hxPopover]="!readonly() && isCollapsed() ? collapsedPopover : undefined"
        [hxTooltip]="!isCollapsed() && node().data?.description ? node().data.description : ''"
        [ngClass]="[contentClass(), inferredStatus()]"
        [ngStyle]="customStyles()"
        [tabindex]="isFocused() ? 0 : -1"
        (click)="onContentClick($event)"
        [stopPropagation]="['click']">
        @if (shouldShowChevron() && config().chevronPosition === 'start') {
          <ng-container [ngTemplateOutlet]="chevron" />
        }
        @if (routeHref(node().route); as href) {
          <a
            class="hx-node__route-link"
            [href]="href"
            [attr.target]="node().externalUrl && node().openInNewTab !== false ? '_blank' : null"
            [attr.rel]="node().externalUrl && node().openInNewTab !== false ? 'noopener noreferrer' : null"
            tabindex="-1"
            (click)="onRouteLinkClick($event)">
            @if (shouldShowIconSpace()) {
              <div class="hx-node__icon-wrapper">
                @if (node().icon) {
                  <hx-icon [svgIcon]="node().icon || null" [ngClass]="inferredStatus() === 'error' ? 'error' : ''" />
                }
              </div>
            }

            <hx-label
              class="hx-node__label"
              [style.display]="isCollapsed() ? 'none' : null"
              fontWeight="Regular"
              [description]="displayLabel()"
              [color]="labelColor()"
              [ngClass]="{ error: inferredStatus() === 'error', success: inferredStatus() === 'success' }"
              [title]="displayLabel()" />

            @if (isCollapsed() && level() === 0) {
              <span class="hx-node__label hx-node__label--sr-only">{{ displayLabel() }}</span>
            }
          </a>
        } @else {
          @if (shouldShowIconSpace()) {
            <div class="hx-node__icon-wrapper">
              @if (node().icon) {
                <hx-icon [svgIcon]="node().icon || null" [ngClass]="inferredStatus() === 'error' ? 'error' : ''" />
              }
            </div>
          }

          <hx-label
            class="hx-node__label"
            [style.display]="isCollapsed() ? 'none' : null"
            fontWeight="Regular"
            [description]="displayLabel()"
            [color]="labelColor()"
            [ngClass]="{ error: inferredStatus() === 'error', success: inferredStatus() === 'success' }"
            [title]="displayLabel()" />
        }

        @if (shouldShowStatusIconsForNode()) {
          <div class="hx-node__actions">
            @if (shouldShowMainActiveIndicator()) {
              <ng-container
                [ngTemplateOutlet]="statusIcon"
                [ngTemplateOutletContext]="{
                  iconClass: 'active-indicator',
                  title: 'Active',
                  fontIcon: 'chevron-double-right',
                  filled: null,
                }" />
            }
            @if (inferredStatus() === 'success') {
              <ng-container
                [ngTemplateOutlet]="statusIcon"
                [ngTemplateOutletContext]="{
                  iconClass: 'success',
                  title: 'Completed',
                  fontIcon: 'check-circle',
                  filled: hasChildren(),
                }" />
            }
            @if (shouldShowErrorIcon()) {
              <ng-container
                [ngTemplateOutlet]="statusIcon"
                [ngTemplateOutletContext]="{
                  iconClass: 'error',
                  title: 'Error',
                  fontIcon: 'exclamation-triangle',
                  filled: hasChildren(),
                }" />
            }

            @if (shouldShowChevron() && config().chevronPosition !== 'start') {
              <ng-container [ngTemplateOutlet]="chevron" />
            }
          </div>
        }
      </div>
    }
  }

  <ng-template #collapsedPopover>
    <div class="hx-side-nav__collapsed-popover">
      <div class="hx-side-nav__collapsed-popover-title">
        {{ displayLabel() }}
      </div>
      @if (node().data?.description) {
        <div class="hx-side-nav__collapsed-popover-description">
          {{ node().data.description }}
        </div>
      }
      @if (hasChildren()) {
        <ul class="hx-side-nav__collapsed-popover-list" role="menu">
          @for (child of node().children; track child.id) {
            @let childStatus = getPopoverStatus(child);
            <li
              class="hx-side-nav__collapsed-popover-item"
              role="none"
              [class.active]="activeNodeId() === child.id"
              [class.disabled]="child.disabled"
              [class.status-success]="childStatus === 'success'"
              [class.status-error]="childStatus === 'error'"
              [style.--node-hover-color]="nodeHoverBackgroundColor() || '#e7f3f4'">
              @if (routeHref(child.route, child); as childHref) {
                <a
                  class="hx-side-nav__collapsed-popover-link"
                  [href]="child.disabled || readonly() ? null : childHref"
                  [attr.target]="child.externalUrl && child.openInNewTab !== false ? '_blank' : null"
                  [attr.rel]="child.externalUrl && child.openInNewTab !== false ? 'noopener noreferrer' : null"
                  [attr.aria-disabled]="child.disabled ? 'true' : null"
                  [attr.tabindex]="child.disabled ? -1 : 0"
                  role="menuitem"
                  (click)="onPopoverRouteLinkClick(child, $event)">
                  @if (child.icon) {
                    <hx-icon class="hx-popover-item-icon" [svgIcon]="child.icon" />
                  }
                  <span class="hx-popover-item-label">{{ child.label }}</span>
                  @if (shouldRenderPopoverActions(child)) {
                    <div class="hx-popover-item-actions">
                      @if (shouldShowPopoverActiveIndicator(child)) {
                        <ng-container
                          [ngTemplateOutlet]="statusIcon"
                          [ngTemplateOutletContext]="{
                            iconClass: 'active-indicator',
                            title: 'Active',
                            fontIcon: 'chevron-double-right',
                            filled: null,
                          }" />
                      }
                      @if (shouldShowPopoverStatusIcon(child, 'success')) {
                        <ng-container
                          [ngTemplateOutlet]="statusIcon"
                          [ngTemplateOutletContext]="{
                            iconClass: 'success',
                            title: 'Completed',
                            fontIcon: 'check-circle',
                            filled: !!child.children?.length,
                          }" />
                      }
                      @if (shouldShowPopoverStatusIcon(child, 'error')) {
                        <ng-container
                          [ngTemplateOutlet]="statusIcon"
                          [ngTemplateOutletContext]="{
                            iconClass: 'error',
                            title: 'Error',
                            fontIcon: 'exclamation-triangle',
                            filled: !!child.children?.length,
                          }" />
                      }
                    </div>
                  }
                </a>
              } @else {
                <div
                  class="hx-side-nav__collapsed-popover-link"
                  role="menuitem"
                  [attr.tabindex]="child.disabled ? -1 : 0"
                  (click)="onPopoverItemClick(child, $event)"
                  (keydown.enter)="onPopoverItemClick(child, $event)"
                  (keydown.space)="onPopoverItemClick(child, $event)">
                  @if (child.icon) {
                    <hx-icon class="hx-popover-item-icon" [svgIcon]="child.icon" />
                  }
                  <span class="hx-popover-item-label">{{ child.label }}</span>
                </div>
              }
            </li>
          }
        </ul>
      }
    </div>
  </ng-template>

  @if (hasChildren()) {
    <ul
      class="hx-node__children"
      role="group"
      [style.display]="isCollapsed() ? 'none' : null"
      [attr.aria-hidden]="isCollapsed() || !isExpanded()"
      [class.expanded]="isExpanded()">
      @for (child of node().children; track child.id; let last = $last) {
        <hx-tree-node
          [activeNodeId]="activeNodeId()"
          [collapsed]="collapsed()"
          [config]="config()"
          [expandedIds]="expandedIds()"
          [focusedNodeId]="focusedNodeId()"
          [isLast]="last"
          [level]="level() + 1"
          [node]="child"
          [nodeTemplate]="nodeTemplate()"
          [readonly]="readonly()"
          [showChildIcons]="showChildIcons()"
          [showConnectorLines]="showConnectorLines()"
          [showStatusIcons]="showStatusIcons()"
          (nodeSelect)="nodeSelect.emit($event)"
          (toggleId)="toggleId.emit($event)" />
      }
    </ul>
  }
</li>
